import { useEffect, useState } from "react";
import { useLang } from "@/lib/i18n";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, FileText, DownloadSimple } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { toast } from "sonner";
import axios from "axios";

const DOC_TYPES = [
  { key: "contrato", labelKey: "doc_contrato" },
  { key: "solicitud_autorizacion", labelKey: "doc_solicitud" },
  { key: "solicitud_prorroga", labelKey: "doc_solicitud_prorroga" },
  { key: "aviso_mensuras", labelKey: "doc_aviso_mensuras" },
  { key: "notificacion_colindante_norte", labelKey: "doc_notif_norte" },
  { key: "notificacion_colindante_sur", labelKey: "doc_notif_sur" },
  { key: "notificacion_colindante_este", labelKey: "doc_notif_este" },
  { key: "notificacion_colindante_oeste", labelKey: "doc_notif_oeste" },
  { key: "aviso_medio_ambiente", labelKey: "doc_aviso_medio_ambiente" },
  { key: "aviso_ayuntamiento", labelKey: "doc_aviso_ayuntamiento" },
  { key: "aviso_tribunal_tierras", labelKey: "doc_aviso_tribunal" },
  { key: "publicacion_periodico", labelKey: "doc_publicacion" },
  { key: "letrero", labelKey: "doc_letrero" },
  { key: "carta_conformidad", labelKey: "doc_carta_conformidad" },
  { key: "declaracion_posesion", labelKey: "doc_declaracion_posesion" },
  { key: "informe_tecnico", labelKey: "doc_informe_tecnico" },
  { key: "acta_hitos", labelKey: "doc_acta_hitos" },
  { key: "resultados_medicion", labelKey: "doc_resultados" },
];

export default function PlantillasTab({ exp, setExp }) {
  const { t } = useLang();
  const [selectedDoc, setSelectedDoc] = useState("contrato");
  const [loadingDefault, setLoadingDefault] = useState(false);
  const clauses = exp.custom_clauses || {};
  const current = clauses[selectedDoc] || "";

  const updateClause = (val) => {
    setExp({
      ...exp,
      custom_clauses: { ...(exp.custom_clauses || {}), [selectedDoc]: val },
    });
  };

  const clearClause = () => {
    const next = { ...(exp.custom_clauses || {}) };
    delete next[selectedDoc];
    setExp({ ...exp, custom_clauses: next });
  };

  const loadDefault = async () => {
    setLoadingDefault(true);
    try {
      const r = await axios.get(`${API}/expedientes/${exp.id}/template-default/${selectedDoc}`);
      updateClause(r.data.text || "");
      toast.success("Plantilla cargada. Edítala y guarda.");
    } catch (e) {
      toast.error("No se pudo cargar la plantilla por defecto");
    } finally { setLoadingDefault(false); }
  };

  return (
    <div className="space-y-6" data-testid="plantillas-tab">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">Plantillas — Texto personalizado</h2>
        <p className="text-slate-600 mt-1">
          Aquí puedes <b>agregar cláusulas o párrafos personalizados</b> a cada documento.
          El texto se inserta antes de la firma. Pulsa <b>Cargar plantilla original</b> para traer el texto actual del documento, modificarlo y guardarlo.
        </p>
      </div>

      <div className="bg-white border border-slate-200">
        <div className="px-5 py-3 border-b border-slate-200 flex flex-wrap items-center gap-3">
          <FileText weight="duotone" size={18} color="#ea580c" />
          <div className="coord-label">Documento</div>
          <Select value={selectedDoc} onValueChange={setSelectedDoc}>
            <SelectTrigger data-testid="plantilla-doc-select" className="rounded-sm w-[420px] max-w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DOC_TYPES.map((d) => {
                const has = !!(clauses[d.key] && clauses[d.key].trim());
                return (
                  <SelectItem key={d.key} value={d.key}>
                    {has ? "●" : "○"}  {t[d.labelKey] || d.key}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <div className="flex-1" />
          <Button
            data-testid="plantilla-load-default-btn"
            size="sm"
            variant="outline"
            onClick={loadDefault}
            disabled={loadingDefault}
            className="rounded-sm"
          >
            <DownloadSimple size={14} className="mr-1.5" />
            {loadingDefault ? "Cargando…" : "Cargar plantilla original"}
          </Button>
          {current.trim() && (
            <Button
              data-testid="plantilla-clear-btn"
              size="sm"
              variant="ghost"
              onClick={clearClause}
              className="text-red-600 rounded-sm"
            >
              Limpiar
            </Button>
          )}
        </div>
        <div className="p-5">
          <Label className="coord-label">Texto personalizado a insertar</Label>
          <Textarea
            data-testid="plantilla-textarea"
            value={current}
            onChange={(e) => updateClause(e.target.value)}
            className="mt-1.5 rounded-sm min-h-[460px] font-mono text-sm"
            placeholder={`Escribe aquí el párrafo o cláusula a añadir a "${t[DOC_TYPES.find(d => d.key === selectedDoc)?.labelKey] || selectedDoc}", o pulsa "Cargar plantilla original" para traer el texto actual y modificarlo.`}
          />
          <p className="text-xs text-slate-500 mt-2">
            <Pencil size={12} className="inline mr-1" />
            Tip: doble salto de línea = nuevo párrafo. Después de guardar, regenera el PDF para ver los cambios. Este texto se inserta al final del documento, antes de la firma.
          </p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 p-4 rounded-sm text-sm text-slate-700">
        <b>Indicadores:</b> ● = doc con texto personalizado &nbsp;·&nbsp; ○ = doc con plantilla por defecto
      </div>
    </div>
  );
}
